import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/** The slice of a `usePaginatedResource`-based list hook this needs. */
export interface ListSource<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  changePage: (page: number) => void;
  refresh: () => void;
}

/** The slice of a `useEntitySearch`-based hook this needs. */
export interface SearchSource<T, E = void> {
  results: T[];
  loading: boolean;
  error: string | null;
  hasSearched: boolean;
  total: number;
  page: number;
  totalPages: number;
  lastSearchTerm: string;
  search: (
    term: string,
    page?: number,
    append?: boolean,
    extra?: E
  ) => Promise<void>;
  clearSearch: () => void;
  changeSearchPage: (page: number) => void;
}

interface Options<T, E> {
  list: ListSource<T>;
  search: SearchSource<T, E>;
  /** From the URL on first render; kicks off a search if present. */
  initialSearchTerm: string;
  initialPage: number;
  /** Per-search filter passed through to every search call (e.g. a challenge id). */
  searchExtra?: E;
  /** Extra query params to keep in the URL. Empty / undefined values are dropped. */
  urlParams?: Record<string, string | number | undefined | null>;
}

/**
 * A paginated list with an independent "search mode", as the programs,
 * recipes and exercises pages present it: one table shows either the
 * browsed page or the search results, and the URL tracks whichever is
 * active.
 */
export const useSearchableList = <T, E = void>({
  list,
  search,
  initialSearchTerm,
  initialPage,
  searchExtra,
  urlParams,
}: Options<T, E>) => {
  const [, setSearchParams] = useSearchParams();
  const {
    hasSearched,
    lastSearchTerm,
    search: runSearch,
    clearSearch,
    changeSearchPage,
    page: searchPage,
  } = search;
  const { changePage, refresh, page: listPage } = list;

  // Resume a search that was in the URL. Runs once, on mount.
  useEffect(() => {
    if (initialSearchTerm && !hasSearched) {
      runSearch(initialSearchTerm, initialPage, false, searchExtra);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Serialised so a fresh object literal per render does not re-run the sync.
  const extraKey = JSON.stringify(urlParams ?? {});

  useEffect(() => {
    const params: Record<string, string> = {};

    if (hasSearched) {
      params.page = searchPage.toString();
      if (lastSearchTerm) params.search = lastSearchTerm;
    } else {
      params.page = listPage.toString();
    }

    const extra = JSON.parse(extraKey) as Record<string, unknown>;
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = String(value);
      }
    }

    setSearchParams(params, { replace: true });
  }, [
    listPage,
    searchPage,
    hasSearched,
    lastSearchTerm,
    extraKey,
    setSearchParams,
  ]);

  const onPageChange = useCallback(
    (page: number) => (hasSearched ? changeSearchPage(page) : changePage(page)),
    [hasSearched, changeSearchPage, changePage]
  );

  const handleRefresh = useCallback(() => {
    if (hasSearched) clearSearch();
    refresh();
  }, [hasSearched, clearSearch, refresh]);

  const handleSearch = useCallback(
    (term: string) => {
      runSearch(term, 1, false, searchExtra);
    },
    [runSearch, searchExtra]
  );

  // After a create / update / delete: refetch the browsed page, and the
  // current search page too when one is showing.
  const refreshAll = useCallback(() => {
    refresh();
    if (hasSearched && lastSearchTerm) {
      runSearch(lastSearchTerm, searchPage, false, searchExtra);
    }
  }, [
    refresh,
    hasSearched,
    lastSearchTerm,
    runSearch,
    searchPage,
    searchExtra,
  ]);

  return {
    items: hasSearched ? search.results : list.items,
    loading: hasSearched ? search.loading : list.loading,
    /** Browse-mode loading only; the Refresh button keys off this. */
    listLoading: list.loading,
    error: hasSearched ? search.error : list.error,
    total: hasSearched ? search.total : list.total,
    page: hasSearched ? searchPage : listPage,
    totalPages: hasSearched ? search.totalPages : list.totalPages,
    hasSearched,
    lastSearchTerm,
    onPageChange,
    handleRefresh,
    handleSearch,
    handleClearSearch: clearSearch,
    refreshAll,
  };
};
